import * as Tone from 'tone';
import { generateLayerRhythm, transformPattern } from '../generators/rhythm';
import type { LayerType } from '../types';

/**
 * Transport - Timing and rhythm engine
 * Manages BPM, 16-step sequencer, and advanced pattern generation
 */
export class Transport {
  private loop: Tone.Loop | null = null;
  private step = 0;
  private readonly STEPS = 16; // 16-step sequencer
  private patterns: Map<LayerType, boolean[]> = new Map();
  private bpm = 120;
  
  // Pattern generation state
  private currentDensity = 50;
  private currentVariation = 50;
  private patternAge: Map<LayerType, number> = new Map(); // Track how long each pattern has been playing
  private maxPatternAge = 32; // Regenerate after this many bars
  private barCount = 0;
  
  // Live variation control (0 = locked/static, 100 = maximum evolution)
  private liveVariation = 30; // Default to moderate variation
  private patternsLocked = false; // When true, patterns never change during playback

  constructor(bpm = 120) {
    this.bpm = bpm;
    this.setBPM(bpm);
    this.generatePatterns();
  }

  /**
   * Start the transport (begin sequencing)
   */
  start(): void {
    if (this.loop) {
      console.warn('⚠️ Transport already running');
      return;
    }

    // Create loop that fires every 16th note
    this.loop = new Tone.Loop((time) => {
      this.onStep(time, this.step);
      this.step = (this.step + 1) % this.STEPS;
    }, '16n'); // 16th note subdivision

    this.loop.start(0);
    Tone.Transport.start();
  }

  /**
   * Stop the transport
   */
  stop(): void {
    if (this.loop) {
      this.loop.stop();
      this.loop.dispose();
      this.loop = null;
    }

    Tone.Transport.stop();
    this.step = 0;
  }

  /**
   * Set BPM (tempo)
   */
  setBPM(bpm: number): void {
    this.bpm = Math.max(60, Math.min(180, bpm)); // Clamp to 60-180 BPM
    Tone.Transport.bpm.value = this.bpm;
  }

  /**
   * Get current BPM
   */
  getBPM(): number {
    return this.bpm;
  }

  /**
   * Called on each step (16th note)
   */
  private onStep(time: number, step: number): void {
    // Track bar count for pattern evolution
    if (step === 0) {
      this.barCount++;
      this.evolvePatterns();
    }
    
    // Dispatch custom event with timing info
    const event = new CustomEvent('transport:step', {
      detail: {
        time, // Tone.js scheduled time (for precise timing)
        step, // Current step (0-15)
        patterns: this.patterns, // All layer patterns
      },
    });

    window.dispatchEvent(event);
  }
  
  /**
   * Evolve patterns over time for more variety
   * Called once per bar
   */
  private evolvePatterns(): void {
    // Skip evolution if patterns are locked or live variation is 0
    if (this.patternsLocked || this.liveVariation === 0) {
      return;
    }
    
    const layers: LayerType[] = ['percussion', 'bass', 'melody', 'pad'];
    
    // Scale evolution probability based on live variation (0-100)
    // At 0: no changes, at 100: frequent changes
    const variationFactor = this.liveVariation / 100;
    
    layers.forEach(layer => {
      const age = (this.patternAge.get(layer) ?? 0) + 1;
      this.patternAge.set(layer, age);
      
      // Base probability scales with variation setting
      // At liveVariation=100: behaves like before (age-based)
      // At liveVariation=10: very rare changes
      const ageContribution = age / this.maxPatternAge;
      const changeThreshold = Math.max(0.005, ageContribution * variationFactor * 0.3);
      
      if (Math.random() < changeThreshold) {
        // Apply a transformation or regenerate
        const currentPattern = this.patterns.get(layer);
        if (currentPattern) {
          const action = Math.random();
          
          // Higher variation = more likely to do full regeneration
          const regenThreshold = 0.2 + (variationFactor * 0.2); // 20-40%
          const transformThreshold = regenThreshold + 0.3; // +30%
          
          if (action < regenThreshold) {
            // Full regeneration
            this.patterns.set(layer, generateLayerRhythm(layer, this.currentDensity, this.currentVariation));
            this.patternAge.set(layer, 0);
          } else if (action < transformThreshold) {
            // Transform existing pattern (subtle change)
            const transformations = ['shift', 'thin', 'thicken', 'humanize'] as const;
            const transform = transformations[Math.floor(Math.random() * transformations.length)];
            this.patterns.set(layer, transformPattern(currentPattern, transform));
          }
          // Rest: no change
        }
      }
    });
  }

  /**
   * Generate rhythm patterns for each layer using advanced algorithms
   */
  private generatePatterns(): void {
    // Use the new advanced rhythm generation
    this.patterns.set('percussion', generateLayerRhythm('percussion', this.currentDensity, this.currentVariation));
    this.patterns.set('bass', generateLayerRhythm('bass', this.currentDensity, this.currentVariation));
    this.patterns.set('melody', generateLayerRhythm('melody', this.currentDensity, this.currentVariation));
    this.patterns.set('pad', generateLayerRhythm('pad', this.currentDensity, this.currentVariation));
    
    // Reset pattern ages
    this.patternAge.set('percussion', 0);
    this.patternAge.set('bass', 0);
    this.patternAge.set('melody', 0);
    this.patternAge.set('pad', 0);
    this.barCount = 0;
  }

  /**
   * Regenerate patterns with variation
   * @param variation - 0-100, affects pattern complexity and algorithm selection
   */
  regeneratePatterns(variation: number): void {
    this.currentVariation = variation;
    this.generatePatterns();
  }

  /**
   * Set density - adjusts how many hits per measure for each layer
   * @param density - 0-100, affects pattern density (0 = silence, 100 = maximum)
   */
  setDensity(density: number): void {
    this.currentDensity = density;
    
    // Generate new patterns with updated density
    this.patterns.set('percussion', generateLayerRhythm('percussion', density, this.currentVariation));
    this.patterns.set('bass', generateLayerRhythm('bass', density, this.currentVariation));
    this.patterns.set('melody', generateLayerRhythm('melody', density, this.currentVariation));
    this.patterns.set('pad', generateLayerRhythm('pad', density, this.currentVariation));
    
    // Reset pattern ages to give new patterns time to develop
    this.patternAge.set('percussion', 0);
    this.patternAge.set('bass', 0);
    this.patternAge.set('melody', 0);
    this.patternAge.set('pad', 0);
  }

  /**
   * Get pattern for specific layer
   */
  getPattern(layer: LayerType): boolean[] | undefined {
    return this.patterns.get(layer);
  }

  /**
   * Set custom pattern for layer
   */
  setPattern(layer: LayerType, pattern: boolean[]): void {
    if (pattern.length !== this.STEPS) {
      console.error(`❌ Pattern must be ${this.STEPS} steps long`);
      return;
    }
    this.patterns.set(layer, pattern);
  }
  
  /**
   * Set live variation amount (how much patterns evolve during playback)
   * @param amount - 0 (static/locked) to 100 (maximum evolution)
   */
  setLiveVariation(amount: number): void {
    this.liveVariation = Math.max(0, Math.min(100, amount));
  }
  
  /**
   * Get current live variation amount
   */
  getLiveVariation(): number {
    return this.liveVariation;
  }
  
  /**
   * Lock/unlock patterns (when locked, patterns never change during playback)
   */
  setPatternLock(locked: boolean): void {
    this.patternsLocked = locked;
  }
  
  /**
   * Check if patterns are locked
   */
  isPatternsLocked(): boolean {
    return this.patternsLocked;
  }

  /**
   * Check if transport is running
   */
  isRunning(): boolean {
    return this.loop !== null;
  }

  /**
   * Get current step (0-15)
   */
  getCurrentStep(): number {
    return this.step;
  }
}
